import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import { useAuth } from '../../../stores/AuthContext';
import styles from './UsersPage.module.css';

interface SuperAdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: number;
  lastLogin: string | null;
  createdAt: string;
}

export default function UsersPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('SUPER_ADMIN');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      const data = await api.get<SuperAdminUser[]>('/api/super-admin/users');
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/api/super-admin/users', { email, password, name, role });
      setShowForm(false);
      setEmail('');
      setPassword('');
      setName('');
      setRole('SUPER_ADMIN');
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await api.put(`/api/super-admin/users/${id}/toggle-status`);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDelete(null);
    try {
      await api.delete(`/api/super-admin/users/${id}`);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Administradores del Sistema</h2>
        {isSuperAdmin && (
          <button className={styles.addBtn} onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancelar' : '+ Nuevo Admin'}
          </button>
        )}
      </div>

      {isSuperAdmin && showForm && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Nuevo Administrador</h3>
          {error && <div className={styles.error}>{error}</div>}
          <form onSubmit={handleCreate} className={styles.form}>
            <div className={styles.formGroup}>
              <label>Nombre</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                minLength={8}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Rol</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={styles.select}
              >
                <option value="SUPER_ADMIN">Super Admin (acceso total)</option>
                <option value="ADMIN">Admin (acceso limitado)</option>
              </select>
            </div>
            <button type="submit" disabled={saving} className={styles.submitBtn}>
              {saving ? 'Creando...' : 'Crear Administrador'}
            </button>
          </form>
        </div>
      )}

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Correo</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Último acceso</th>
              <th>Creado</th>
              {isSuperAdmin && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isSuperAdmin ? 7 : 6} className={styles.center}>
                  Cargando...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={isSuperAdmin ? 7 : 6} className={styles.center}>
                  Sin administradores registrados
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.name}</strong>
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <span
                      className={u.role === 'SUPER_ADMIN' ? styles.superBadge : styles.adminBadge}
                    >
                      {u.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin'}
                    </span>
                  </td>
                  <td>
                    <span className={u.isActive ? styles.activeBadge : styles.inactiveBadge}>
                      {u.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Nunca'}</td>
                  <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                  {isSuperAdmin && (
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={u.isActive ? styles.warnBtn : styles.successBtn}
                          onClick={() => handleToggleStatus(u.id)}
                          title={u.isActive ? 'Desactivar' : 'Activar'}
                        >
                          {u.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        {confirmDelete === u.id ? (
                          <span className={styles.confirmGroup}>
                            <span className={styles.confirmText}>¿Eliminar?</span>
                            <button className={styles.dangerBtn} onClick={() => handleDelete(u.id)}>
                              Sí
                            </button>
                            <button
                              className={styles.cancelBtn}
                              onClick={() => setConfirmDelete(null)}
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            className={styles.deleteBtn}
                            onClick={() => setConfirmDelete(u.id)}
                            title="Eliminar"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
